import { FC, useState, useEffect } from "react";
import { Alert, Form, Tabs, Tab, Badge, Button, Spinner, ListGroup } from "react-bootstrap";
import { NetworkAccountService } from "../../../shared/services/contentNetwork/networkAccounts/networkAccountService";
import showAlert from "../../../shared/utils/generic";
import { NetworkResource } from "@models/networkResource";

interface Props {
    resource: Partial<NetworkResource>;
    onChange: (resource: Partial<NetworkResource>) => void;
    showAuthKeys?: boolean;
    showLinking?: boolean;
    organizationId?: number;
    resourceNetwork?: string;
    resourceId?: number;
    onLinksChanged?: () => void;
}

// Networks whose app credentials live in server settings rather than on each resource, so
// nobody has to paste a secret into a browser to connect an account. Meta has always worked this
// way; LinkedIn and Vibe now do too, and Snapchat joined them in MPN-1484 — its box used to ask
// for a refresh_token, which is how an employee's personal token ended up being the credential
// the whole integration depended on.
const CREDENTIALS_FROM_SERVER = ["meta", "linkedin", "vibe", "snapchat"];

// For the networks that still expect a pasted blob, name the keys they actually use. A generic
// placeholder is worse than none: the old one showed access_token and refresh_token everywhere,
// which are what the authorize flow returns, not anything a person can supply.
const CREDENTIAL_PLACEHOLDER: Record<string, string> = {
    pinterest: '{"app_id": "...", "app_secret": "..."}',
    google: '{"client_id": "...", "client_secret": "..."}',
};

const DEFAULT_CREDENTIAL_PLACEHOLDER = '{"client_id": "...", "client_secret": "..."}';

const NetworkResourceForm: FC<Props> = ({
    resource,
    onChange,
    showAuthKeys = false,
    showLinking = false,
    organizationId,
    resourceNetwork,
    onLinksChanged,
}) => {
    const [authKeysText, setAuthKeysText] = useState("");
    const [authKeysError, setAuthKeysError] = useState("");
    const [activeTab, setActiveTab] = useState("basic");

    // Linking state
    const [availableAccounts, setAvailableAccounts] = useState<NetworkResource[]>([]);
    const [linkedParents, setLinkedParents] = useState<NetworkResource[]>([]);
    const [linkedChildren, setLinkedChildren] = useState<NetworkResource[]>([]);
    const [loadingLinks, setLoadingLinks] = useState(false);
    const [linkingInProgress, setLinkingInProgress] = useState<number | null>(null);

    const accountService = new NetworkAccountService();

    const network = (resourceNetwork ?? resource.network ?? "").toLowerCase();
    const credentialsFromServer = CREDENTIALS_FROM_SERVER.includes(network);
    const credentialPlaceholder =
        CREDENTIAL_PLACEHOLDER[network] ?? DEFAULT_CREDENTIAL_PLACEHOLDER;

    // Note: response payloads no longer include auth_keys (security fix). The textarea
    // stays empty in edit mode unless the user explicitly pastes new credentials.

    useEffect(() => {
        if (showLinking && organizationId && resourceNetwork && resource) {
            let stale = false;
            loadLinkingData(() => stale);
            return () => {
                stale = true;
            };
        }
    }, [resource]);

    // isStale guards against out-of-order responses: a slower run for a previous
    // `resource` must not overwrite the state of the run for the current one.
    const loadLinkingData = async (isStale: () => boolean) => {
        if (!organizationId || !resourceNetwork || !resource) return;

        setLoadingLinks(true);
        try {
            // Get all account resources for this network and organization
            const accounts = await accountService.listNetworkResources({
                organization_ids: [organizationId, 4],
                types: ["account"],
            });

            // Filter to only this network and exclude self
            const filteredAccounts = accounts.filter(
                (acc) => acc.network.toLowerCase() === resourceNetwork.toLowerCase() && acc.id !== resource.id
            );
            if (isStale()) return;
            setAvailableAccounts(filteredAccounts);

            // Get linked parents
            if (resource.parent_ids && resource.parent_ids.length > 0) {
                const parents = await accountService.listNetworkResources({
                    resource_ids: resource.parent_ids,
                });
                if (isStale()) return;
                setLinkedParents(parents);
            } else {
                setLinkedParents([]);
            }

            // Get linked children
            if (resource.child_ids && resource.child_ids.length > 0) {
                const children = await accountService.listNetworkResources({
                    resource_ids: resource.child_ids,
                });
                if (isStale()) return;
                setLinkedChildren(children);
            } else {
                setLinkedChildren([]);
            }
        } catch (err) {
            console.error("Failed to load linking data:", err);
            showAlert("Failed to load linked resources", "error", 3000);
        } finally {
            if (!isStale()) {
                setLoadingLinks(false);
            }
        }
    };

    const handleAuthKeysChange = (value: string) => {
        setAuthKeysText(value);
        setAuthKeysError("");

        if (!value.trim()) {
            // Strip auth_keys so PATCH doesn't overwrite stored credentials with {}.
            const { auth_keys: _omit, ...rest } = resource;
            onChange(rest);
            return;
        }

        try {
            const parsed = JSON.parse(value);
            onChange({ ...resource, auth_keys: parsed });
        } catch {
            setAuthKeysError("Invalid JSON format");
        }
    };

    const handleLinkResource = async (accountId: number) => {
        if (!resource.id) return;

        setLinkingInProgress(accountId);
        try {
            // Child is the current resource, parent is the account we're linking to
            await accountService.linkNetworkResource(resource.id, accountId);
            showAlert("Resource linked successfully", "success", 2000);

            // Update the resource state to reflect the new parent
            // onChange triggers the linking-data effect with the fresh ids
            const updatedParentIds = [...(resource.parent_ids || []), accountId];
            onChange({ ...resource, parent_ids: updatedParentIds });
            onLinksChanged?.();
        } catch (err) {
            console.error("Failed to link resource:", err);
            showAlert("Failed to link resource", "error", 3000);
        } finally {
            setLinkingInProgress(null);
        }
    };

    const handleUnlinkParent = async (parentId: number) => {
        if (!resource.id) return;

        setLinkingInProgress(parentId);
        try {
            await accountService.unlinkNetworkResource(resource.id, parentId);
            showAlert("Resource unlinked successfully", "success", 2000);

            // Update the resource state to remove the parent
            const updatedParentIds = (resource.parent_ids || []).filter(id => id !== parentId);
            onChange({ ...resource, parent_ids: updatedParentIds });
            onLinksChanged?.();
        } catch (err) {
            console.error("Failed to unlink resource:", err);
            showAlert("Failed to unlink resource", "error", 3000);
        } finally {
            setLinkingInProgress(null);
        }
    };

    const handleUnlinkChild = async (childId: number) => {
        if (!resource.id) return;

        setLinkingInProgress(childId);
        try {
            await accountService.unlinkNetworkResource(childId, resource.id);
            showAlert("Resource unlinked successfully", "success", 2000);

            // Update the resource state to remove the child
            const updatedChildIds = (resource.child_ids || []).filter(id => id !== childId);
            onChange({ ...resource, child_ids: updatedChildIds });
            onLinksChanged?.();
        } catch (err) {
            console.error("Failed to unlink resource:", err);
            showAlert("Failed to unlink resource", "error", 3000);
        } finally {
            setLinkingInProgress(null);
        }
    };

    const isLinked = (accountId: number) => {
        return linkedParents.some((p) => p.id === accountId);
    };

    const renderBasicInfo = () => (
        <div>
            <Form.Group className="mb-3">
                <Form.Label>
                    Resource Name <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                    type="text"
                    placeholder="Enter resource name"
                    value={resource.name || ""}
                    onChange={(e) => onChange({ ...resource, name: e.target.value })}
                    required
                />
            </Form.Group>

            <Form.Group className="mb-3">
                <Form.Label>
                    External ID <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                    type="text"
                    placeholder="Enter external ID from the network"
                    value={resource.external_id || ""}
                    onChange={(e) => onChange({ ...resource, external_id: e.target.value })}
                    required
                />
                <Form.Text className="text-muted">
                    The ID from the ad network (e.g., account ID, page ID, board ID)
                </Form.Text>
            </Form.Group>

            {showAuthKeys && credentialsFromServer && (
                <Alert variant={resource.has_credentials ? "success" : "info"} className="py-2 px-3 small">
                    <i className={`bi ${resource.has_credentials ? "bi-shield-check" : "bi-info-circle"} me-2`}></i>
                    {resource.has_credentials
                        ? "Connected. Credentials are held on the server."
                        : "No credentials needed here. Save this resource, then use Connect to authorize it."}
                </Alert>
            )}

            {/*
                Vibe's consent screen has one setting that decides whether this connection keeps
                working as institutions are added. Ticking "automatically grant access to future
                advertisers" issues a token covering the whole account; leaving it off issues one
                covering only the advertisers picked that day, so every new school then needs its
                own consent, and until someone does it publishing fails partway with a 404 that
                reads like a deleted advertiser.

                Said here rather than in the docs because the only moment it can be acted on is
                the one right before someone clicks Connect.
            */}
            {showAuthKeys && network === "vibe" && !resource.has_credentials && (
                <Alert variant="warning" className="py-2 px-3 small">
                    <i className="bi bi-exclamation-triangle me-2"></i>
                    On Vibe&apos;s consent screen, turn on{" "}
                    <strong>Automatically grant access to future advertisers</strong>. Without it
                    this connection only covers the advertisers you tick today, and every school
                    added later needs its own reconnect before it can publish.
                </Alert>
            )}

            {showAuthKeys && !credentialsFromServer && (
                <Form.Group className="mb-3">
                    <Form.Label className="d-flex align-items-center gap-2">
                        Authentication Keys (JSON)
                        {resource.has_credentials ? (
                            <Badge bg="success" className="px-2">
                                <i className="bi bi-shield-check me-1"></i>
                                Credentials stored
                            </Badge>
                        ) : (
                            <Badge bg="secondary" className="px-2">
                                <i className="bi bi-shield-exclamation me-1"></i>
                                Not connected
                            </Badge>
                        )}
                    </Form.Label>
                    {resource.has_credentials && (
                        <Alert variant="info" className="py-2 px-3 mb-2 small">
                            <i className="bi bi-info-circle me-2"></i>
                            Existing credentials are stored on the server and cannot be displayed. Paste a new JSON object below only if you want to replace them.
                        </Alert>
                    )}
                    <Form.Control
                        as="textarea"
                        rows={6}
                        placeholder={credentialPlaceholder}
                        value={authKeysText}
                        onChange={(e) => handleAuthKeysChange(e.target.value)}
                        isInvalid={!!authKeysError}
                        style={{ fontFamily: "monospace", fontSize: "0.9rem" }}
                    />
                    {authKeysError && (
                        <Form.Control.Feedback type="invalid">{authKeysError}</Form.Control.Feedback>
                    )}
                    <Form.Text className="text-muted">
                        {resource.has_credentials
                            ? "Paste a new JSON object only if you want to replace the stored set."
                            : "Paste this network's app credentials as JSON, then connect."}
                    </Form.Text>
                </Form.Group>
            )}
        </div>
    );

    const renderLinkedResources = () => {
        if (loadingLinks) {
            return (
                <div className="text-center py-4">
                    <Spinner animation="border" size="sm" />
                    <p className="text-muted mt-2 mb-0">Loading linked resources...</p>
                </div>
            );
        }

        /*
            What is already linked comes first — that is what you open this tab
            to check. Available accounts are the action you take afterwards, so
            they sit at the bottom.
        */
        const availableToLink = availableAccounts.filter((acc) => !isLinked(acc.id));

        return (
            <div>
                {/* Linked Parents */}
                <div className="mb-4">
                    <div className="d-flex align-items-center justify-content-between mb-2">
                        <h6 className="mb-0">
                            <i className="bi bi-arrow-up-circle me-2"></i>
                            Linked Accounts (Parents)
                        </h6>
                        <Badge bg="secondary">{linkedParents.length}</Badge>
                    </div>
                    {linkedParents.length === 0 ? (
                        <div className="text-muted small p-3 bg-light rounded">
                            No parent accounts linked
                        </div>
                    ) : (
                        <ListGroup>
                            {linkedParents.map((parent) => (
                                <ListGroup.Item
                                    key={parent.id}
                                    className="d-flex align-items-center justify-content-between"
                                >
                                    <div className="d-flex align-items-center gap-2">
                                        <i className="bi bi-person-circle text-muted"></i>
                                        <div>
                                            <div><strong>{parent.name}</strong></div>
                                            <small className="text-muted">
                                                <i className="bi bi-hash"></i>
                                                {parent.external_id}
                                            </small>
                                        </div>
                                    </div>
                                    <Button
                                        variant="outline-danger"
                                        size="sm"
                                        onClick={() => handleUnlinkParent(parent.id)}
                                        disabled={linkingInProgress === parent.id}
                                    >
                                        {linkingInProgress === parent.id ? (
                                            <Spinner animation="border" size="sm" />
                                        ) : (
                                            <i className="bi bi-x-lg"></i>
                                        )}
                                    </Button>
                                </ListGroup.Item>
                            ))}
                        </ListGroup>
                    )}
                </div>

                {/* Linked Children */}
                <div className="mb-4">
                    <div className="d-flex align-items-center justify-content-between mb-2">
                        <h6 className="mb-0">
                            <i className="bi bi-arrow-down-circle me-2"></i>
                            Linked Resources (Children)
                        </h6>
                        <Badge bg="secondary">{linkedChildren.length}</Badge>
                    </div>
                    {linkedChildren.length === 0 ? (
                        <div className="text-muted small p-3 bg-light rounded">
                            No child resources linked
                        </div>
                    ) : (
                        <ListGroup>
                            {linkedChildren.map((child) => (
                                <ListGroup.Item
                                    key={child.id}
                                    className="d-flex align-items-center justify-content-between"
                                >
                                    <div className="d-flex align-items-center gap-2">
                                        <i className="bi bi-circle text-muted"></i>
                                        <div>
                                            <div>
                                                <strong>{child.name}</strong>
                                                <Badge bg="light" text="dark" className="ms-2">
                                                    {child.type}
                                                </Badge>
                                            </div>
                                            <small className="text-muted">
                                                <i className="bi bi-hash"></i>
                                                {child.external_id}
                                            </small>
                                        </div>
                                    </div>
                                    <Button
                                        variant="outline-danger"
                                        size="sm"
                                        onClick={() => handleUnlinkChild(child.id)}
                                        disabled={linkingInProgress === child.id}
                                    >
                                        {linkingInProgress === child.id ? (
                                            <Spinner animation="border" size="sm" />
                                        ) : (
                                            <i className="bi bi-x-lg"></i>
                                        )}
                                    </Button>
                                </ListGroup.Item>
                            ))}
                        </ListGroup>
                    )}
                </div>

                {/* Available Accounts to Link */}
                <div>
                    <div className="d-flex align-items-center justify-content-between mb-2">
                        <h6 className="mb-0">
                            <i className="bi bi-plus-circle me-2"></i>
                            Available Accounts
                        </h6>
                        <Badge bg="secondary">{availableToLink.length}</Badge>
                    </div>
                    {availableToLink.length === 0 ? (
                        <div className="text-muted small p-3 bg-light rounded">
                            No available accounts to link
                        </div>
                    ) : (
                        <ListGroup>
                            {availableToLink.map((account) => (
                                <ListGroup.Item
                                    key={account.id}
                                    className="d-flex align-items-center justify-content-between"
                                >
                                    <div className="d-flex align-items-center gap-2">
                                        <i className="bi bi-person-circle text-muted"></i>
                                        <div>
                                            <div><strong>{account.name}</strong></div>
                                            <small className="text-muted">
                                                <i className="bi bi-hash"></i>
                                                {account.external_id}
                                            </small>
                                        </div>
                                    </div>
                                    <Button
                                        variant="outline-primary"
                                        size="sm"
                                        onClick={() => handleLinkResource(account.id)}
                                        disabled={linkingInProgress === account.id}
                                    >
                                        {linkingInProgress === account.id ? (
                                            <Spinner animation="border" size="sm" />
                                        ) : (
                                            <>
                                                <i className="bi bi-link-45deg me-1"></i>
                                                Link
                                            </>
                                        )}
                                    </Button>
                                </ListGroup.Item>
                            ))}
                        </ListGroup>
                    )}
                </div>
            </div>
        );
    };

    if (!showLinking) {
        return renderBasicInfo();
    }

    return (
        <Tabs activeKey={activeTab} onSelect={(k) => setActiveTab(k || "basic")} className="mb-3">
            <Tab eventKey="basic" title="Basic Info">
                {renderBasicInfo()}
            </Tab>
            <Tab
                eventKey="linked"
                title={
                    <span>
                        Linked Resources
                        {(linkedParents.length + linkedChildren.length > 0) && (
                            <Badge bg="primary" className="ms-2">
                                {linkedParents.length + linkedChildren.length}
                            </Badge>
                        )}
                    </span>
                }
            >
                {renderLinkedResources()}
            </Tab>
        </Tabs>
    );
};

export default NetworkResourceForm;